import { PeriodChip, TypeChip } from "@/components/common/report-chip";
import { cn } from "@/lib/utils";
import { Button } from "@/toggle-ui-kit/button";
import IconButton from "@/toggle-ui-kit/icon-button";
import { ChevronDown, EllipsisVertical } from "lucide-react";
import { NewDot } from "./new-dot";
import { ReportMeta } from "./report-meta";
import { ReportGroupData } from "./types";

interface ReportGroupRowProps {
  group: ReportGroupData;
  isExpanded: boolean;
  onToggle: () => void;
  onVersionUpdate?: () => void;
  /** ⋮ 더보기 컨트롤 (container가 드롭다운을 주입). 없으면 기본 버튼. */
  menu?: React.ReactNode;
  /** 작성자 이름 조회 중이면 이메일 대신 스켈레톤을 보여준다. */
  isAuthorLoading?: boolean;
}

// ============================================
// ReportGroupRow
// 보고서 그룹의 메인 행(최신 버전). 접기 화살표 · 유형 뱃지 · 이름 ·
// 메타 · "버전 업데이트" · 프리뷰 · 더보기.
//
// "보고서 열기" 클릭 영역은 이 행이 아니라 그룹 카드(ReportGroup)가 오버레이 버튼으로
// 갖는다. 여기서는 자체 동작이 있는 컨트롤(토글·버전 업데이트·⋮)만 z-10 으로 그 위에
// 올려 각자 동작을 지킨다. 버튼이 중첩되지 않아 stopPropagation 배선이 필요 없다.
// ============================================

export const ReportGroupRow = ({
  group,
  isExpanded,
  onToggle,
  onVersionUpdate,
  menu,
  isAuthorLoading,
}: ReportGroupRowProps) => {
  const { latest, financialStatementType, reportPeriod, olderVersions, isNew } =
    group;
  const hasOlderVersions = olderVersions.length > 0;

  return (
    <div className="flex items-center gap-4 py-1 pr-3">
      {hasOlderVersions ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? "버전 접기" : "버전 펼치기"}
          className="relative z-10 flex size-6 shrink-0 items-center justify-center rounded-md text-label-alternative hover:bg-neutral-950/8"
        >
          <ChevronDown
            className={cn(
              "size-5 transition-transform duration-200",
              isExpanded ? "rotate-0" : "-rotate-90",
            )}
          />
        </button>
      ) : (
        // 내부 버전이 없으면 토글 자체를 없애되, 자리(24px)는 메타 열 정렬을 위해 유지한다.
        <div className="size-6 shrink-0" aria-hidden="true" />
      )}

      <div className="flex flex-1 min-w-0 items-center gap-8">
        <div className="flex flex-1 min-w-0 items-center gap-1">
          <div className="flex flex-1 min-w-0 items-center gap-2">
            <div className="flex shrink-0 items-center gap-1">
              {financialStatementType && (
                <TypeChip type={financialStatementType} size="large" />
              )}
              {reportPeriod && (
                <PeriodChip period={reportPeriod} size="large" />
              )}
            </div>
            <span className="relative flex min-w-0 items-center">
              <span className="min-w-0 truncate body-2-normal-medium text-label-normal">
                {latest.name}
              </span>
              {isNew && (
                <>
                  <span className="sr-only">새로 업로드됨</span>
                  <NewDot />
                </>
              )}
            </span>
          </div>
          <ReportMeta meta={latest} isAuthorLoading={isAuthorLoading} />
        </div>

        {/* Figma 시안대로 내용폭(shrink-0) — 오른쪽 정렬.
              배치용 상자는 pointer-events-none 이라 버튼 사이 여백을 눌러도 행 열기로
              통과하고, 실제 컨트롤만 pointer-events-auto 로 되살린다. */}
        <div className="pointer-events-none relative z-10 flex shrink-0 items-center justify-end gap-3">
          <Button
            variant="outlined"
            size="base"
            onClick={onVersionUpdate}
            className="pointer-events-auto"
          >
            버전 업데이트
          </Button>
          <div className="pointer-events-auto">
            {menu ?? (
              <IconButton
                variant="transparent"
                icon={<EllipsisVertical className="size-4" />}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
